import colors from '@shared/design-tokens/colors';
import useMediaQuery from '@mui/material/useMediaQuery';
import { createTheme, alpha } from '@mui/material/styles';
import { CssBaseline, ThemeProvider } from '@mui/material';

const shrinkSizing = false;

/**
 * Has to be used in a component to get the current screen size
 * @returns {boolean} true if the current screen size is mobile
 */
export const isMobileMode = () => {
  const theme = createTheme();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isSmall = useMediaQuery(theme.breakpoints.only('xs'));

  return isSmall;
};

const gray = colors.core.gray ?? '#828282'; // Fallback to default gray
const grayContrastText = '#ffffff';

const forceSmall = false;

const _convertColors = (color, contrastText) => ({
  main: color,
  light: alpha(color, 0.5),
  dark: alpha(color, 0.5),
  contrastText,
});

const _smallInputs = {
  MuiTextField: {
    defaultProps: {
      size: 'small', // Set TextField to have 'small' size by default
    },
  },
  MuiFormControl: {
    defaultProps: {
      size: 'small', // Set FormControl (and thus Select) to have 'small' size by default
    },
  },
  MuiAutocomplete: {
    defaultProps: {
      size: 'small', // Make Autocomplete small by default
    },
  },
};

const customSwitch = {
  styleOverrides: {
    switchBase: {
      color: 'gray',
      '&.Mui-checked': {
        color: 'green',
      },
      '&.Mui-disabled': {
        color: 'darkgray',
      },
    },
    track: {
      '&:before': {
        left: '12px',
        backgroundImage: 'url(\'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="white" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>\')',
      },
      '&:after': {
        backgroundImage: '',
      },
      opacity: 0.2,
      backgroundColor: '',
      '.Mui-checked.Mui-checked + &': {
        opacity: 0.7,
        backgroundColor: 'green',
      },
      '.Mui-disabled + &': {
        opacity: 0.2,
        backgroundColor: 'darkgray',
        '&:before': {
          backgroundImage: '',
        },
        '&:after': {
          // backgroundImage: 'url(\'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="white" d="M19,13H5V11H19V13Z" /></svg>\')',
          // right: '12px',
          // display: 'block', // make it visible
        },
      },
      '.Mui-disabled.Mui-checked + &': {
        opacity: 0.2,
        backgroundColor: 'darkgray',
      },
      '&.Mui-disabled': {
        backgroundColor: 'darkgray !important',
      },
    },
    root: {
      padding: 8,
      '& .Mui-checked .MuiSwitch-thumb': {
        backgroundColor: 'green !important',
      },
      '& .Mui-disabled .MuiSwitch-thumb': {
        backgroundColor: 'darkgray !important',
      },
      '& .MuiSwitch-thumb': {
        backgroundColor: 'gray !important',
        boxShadow: 'none',
        width: 16,
        height: 16,
        margin: 2,
      },
      '& .MuiSwitch-track': {
        borderRadius: 11,
        '&:before, &:after': {
          content: '""',
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          width: 16,
          height: 16,
        },
      },
    },
  },
};

export const themeOptions = {
  MD3: {
    color: {
      surface: '#fbf8ff',
      onSurface: '#1b1b21',
      surfaceBright: '#fbf8ff',

      surfaceContainer: '#f0ecf4',
      surfaceContainerLowest: '#ffffff',
      surfaceContainerHigh: '#eae7ef',
      
      tertiary: '#735187',
      onTertiary: '#ffffff',

      tertiaryContainer: '#f5d9ff',
      onTertiaryContainer: '#2c0b3f',

      secondary: '#5c5d72',
      onSecondary: '#ffffff',
      
      secondaryContainer: '#e1e0f9',
      onSecondaryContainer: '#191a2c',

      primaryContainer: '#b3b7ff',
      onPrimaryContainer: '#22266e',

      outline: '#787680',
      primaryFill: '#dbd6eb',
      secondaryFill: '#ccc5e3',
    }
  },
  palette: {
    mode: 'light',
    custom: {
      warning: colors?.core?.callToAction,
    },
    primary: {
      main: colors?.core?.callToAction,
    },
    secondary: {
      main: colors?.core?.darkBlue,
    },
    error: {
      light: '#ef5350', // standard mui
      main: '#d32f2f', // standard mui
      dark: '#c62828', // standard mui
    },
    coral: {
      ..._convertColors(colors?.core?.coral, '#fff'),
    },
    black: {
      main: colors?.core?.black,
    },
    gray: {
      ..._convertColors(gray, grayContrastText),
    },
    text: {
      secondary: "#44474e"
    }
  },
  components: {
    ...(forceSmall ? _smallInputs : {}),
    MuiSwitch: customSwitch,
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          color: 'black',
          ':hover': {
            backgroundColor: `${colors.core.darkBlue}16`,
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          '& *:focus': {
            outline: 'none',
          },
        },
      },
    },
    MuiCssBaseline: {
      // styleOverrides: `
      //   body[data-mui-popper="true"] {
      //     overflow: auto !important;
      //   }
      //   'body.modal-open': {
      //     overflow: 'auto !important',
      //   },
      //   html {
      //     scrollbar-width: auto; /* For Firefox */
      //   },
      //   body, html {
      //     overflow-y: scroll !important; /* For webkit browsers */
      //   }
      //   /* For IE, Edge */
      //   body {
      //     -ms-overflow-style: scrollbar;
      //   }
      // `,
    },
  },
  typography: {
    fontSize: shrinkSizing ? 12.5 : undefined,
    paragraph: {
      'font-size': '14px',
      'font-style': 'normal',
    },
    label: {
      'font-size': '16px',
      'font-style': 'normal',
      'font-weight': '500px !important',
    },
    // h1
    // h2
    // h3
    // h4
    // h5
    // h6
    titleMedium: {
      fontSize: '16px',
      lineHeight: '24px'
    },
    // subtitle1
    // subtitle2
    // body1
    // body2
    // button
    // overline
  },
  spacing: shrinkSizing ? 6 : undefined,

};

export const createLightTheme = () => {
  const theme = createTheme(themeOptions);

  return theme;
};

export const lightTheme = createLightTheme();

// eslint-disable-next-line react/prop-types
const DefaultThemeProvider = ({ children }) => (
  <ThemeProvider theme={lightTheme}>
    <CssBaseline />
    {children}
  </ThemeProvider>
);

export default DefaultThemeProvider;
